/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task, waitForQueue, timeout } from 'ember-concurrency';
import { saveAs } from 'file-saver';
import { dasherize } from '@ember/string';
// @ts-ignore ember-svg-jar merges the helpers with the app, so we can import it
// from the cio/ directory structure. ts-ignore because we don't have type
// definitions for that
import { svgJar } from 'cio/helpers/svg-jar';
import CampaignModel from 'cio/models/campaign';
import AlertsService from 'cio/services/alerts';
import RavenService from 'cio/services/raven';
import { GRAY_100, GRAY_300 } from 'cio/utils/colors';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import AnalyticsService from 'cio/services/analytics';
import { VWBContext } from 'cio/utils/visual-workflow-builder/context';

interface VWBExportAsImageArgs {
  campaign: CampaignModel;
  rootElement: HTMLDivElement | null;
  canvasElement: HTMLDivElement | null;
  graphSize: { width: number; height: number };
  trackInViewport: boolean;
  tab: VWBTab;
  vwbContext: VWBContext;
}

const IMAGE_PADDING = 70;

export default class VWBExportAsImage extends Component<VWBExportAsImageArgs> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service raven!: RavenService;

  get stickyNotesManager() {
    return this.args.vwbContext.stickyNotesManager;
  }

  get zoomManager() {
    return this.args.vwbContext.zoomManager;
  }

  @tracked isExportingImage = false;

  hasRenderedAllItems = false;
  previousRenderCheckTimestamp = 0;

  get isMetricsTab() {
    return this.args.tab === VWBTab.Metrics;
  }

  @action
  scheduleRenderCheck() {
    this.hasRenderedAllItems = this.areAllNodesRendered();

    if (this.hasRenderedAllItems) {
      return;
    }

    if (this.previousRenderCheckTimestamp !== 0) {
      cancelAnimationFrame(this.previousRenderCheckTimestamp);
      this.previousRenderCheckTimestamp = 0;
    }
    this.previousRenderCheckTimestamp = requestAnimationFrame(this.scheduleRenderCheck);
  }

  private areAllNodesRendered() {
    let { canvasElement } = this.args;
    let { isMetricsTab } = this;

    if (canvasElement == null) {
      return false;
    }

    let nodes = canvasElement.querySelectorAll('[data-vwb-node]:not([data-node-type="virtual_drop_area"])');
    let isAnyNodeStillRendering = Array.from(nodes).some((node) => {
      let isStillRendering = node.getAttribute('data-is-visible') === 'false';

      if (isStillRendering && !isMetricsTab) {
        return true;
      }

      let metricPanels = node.querySelectorAll(
        '[data-vwb-workflow-action-metrics-panel],[data-vwb-path-label-metrics-panel]',
      );

      let areAnyMetricPanelsStillRendering = Array.from(metricPanels).some((metricPanel) => {
        let isStillRendering = metricPanel.getAttribute('data-is-visible') === 'false';
        let isFetchingMetrics = metricPanel.getAttribute('data-is-fetching-metrics') === 'true';
        return isStillRendering || isFetchingMetrics;
      });

      return areAnyMetricPanelsStillRendering;
    });

    return !isAnyNodeStillRendering;
  }

  originalZoomLevel = this.zoomManager.zoomLevel;
  originalCanvasScrollPosition = {
    top: 0,
    left: 0,
  };
  shouldResetZoomLevel = false;

  ensureWorkflowRendered = task({ drop: true }, async () => {
    // For large workflows, we don't render all items for performance reasons.
    // When exporting an image, we need to zoom out fully and wait for all
    // actions to render before generating the image
    let { canvasElement, trackInViewport } = this.args;
    let { isMetricsTab, hasRenderedAllItems, zoomManager } = this;
    let { zoomLevel } = zoomManager;

    let shouldRenderWholeWorkflow = (trackInViewport || isMetricsTab) && !hasRenderedAllItems;
    if (!shouldRenderWholeWorkflow) {
      return;
    }

    if (canvasElement == null) {
      return;
    }
    let { scrollLeft, scrollTop } = canvasElement;

    this.originalZoomLevel = zoomLevel;
    this.originalCanvasScrollPosition = {
      top: scrollTop,
      left: scrollLeft,
    };
    this.shouldResetZoomLevel = true;
    zoomManager.zoomToFit({ skipTrack: true });

    // Wait a little bit to allow the VWB to catch up on the initial zoom out
    await timeout(100);
    await waitForQueue('afterRender');

    this.scheduleRenderCheck();

    while (!this.hasRenderedAllItems) {
      await timeout(100);
    }
  });

  resetZoomIfNecessary = task({ drop: true }, async () => {
    let { canvasElement } = this.args;
    let { shouldResetZoomLevel, originalZoomLevel, originalCanvasScrollPosition, zoomManager } = this;

    if (!shouldResetZoomLevel || canvasElement == null) {
      return;
    }
    // Reset zoom to original level and scroll position so customers don't lose
    // their place
    zoomManager.zoomToValue(originalZoomLevel, { skipTrack: true });
    await waitForQueue('afterRender');
    canvasElement.scrollLeft = originalCanvasScrollPosition.left;
    canvasElement.scrollTop = originalCanvasScrollPosition.top;

    this.shouldResetZoomLevel = false;
  });

  private getSize() {
    let { graphSize } = this.args;

    let { width, height, offsetTop, offsetLeft } = this.adjustSizeForStickyNotes(graphSize.width, graphSize.height);

    return {
      width: width + IMAGE_PADDING * 2,
      height: height + IMAGE_PADDING * 2,
      offsetTop,
      offsetLeft,
    };
  }

  private adjustSizeForStickyNotes(width: number, height: number) {
    let { canvasElement } = this.args;

    let offsetTop = IMAGE_PADDING;
    let offsetLeft = IMAGE_PADDING;

    if (canvasElement == null || !this.stickyNotesManager.isShowingStickyNotes) {
      return { width, height, offsetTop, offsetLeft };
    }

    // Sticky notes can be positioned outside of the graph container, but
    // still relative to its coordinate system. This means a sticky note can
    // have negative coordinates, or coordinates that exceed the graph
    // container's dimensions.
    // If sticky notes are to be included, we need to extend the size of the
    // canvas to make sure they don't get cut off when taking the screenshot.
    let xExtent = {
      min: 0,
      max: width,
    };
    let yExtent = {
      min: 0,
      max: height,
    };

    let notes = canvasElement.querySelectorAll('[data-vwb-sticky-note]');
    if (notes.length > 0) {
      notes.forEach((note) => {
        let noteX = parseInt(note.getAttribute('data-x') ?? '0');
        let noteY = parseInt(note.getAttribute('data-y') ?? '0');
        let noteWidth = parseInt(note.getAttribute('data-width') ?? '0');
        let noteHeight = parseInt(note.getAttribute('data-height') ?? '0');

        let edgeCoordinates = {
          top: noteY - noteHeight / 2,
          bottom: noteY + noteHeight / 2,
          right: noteX + noteWidth / 2,
          left: noteX - noteWidth / 2,
        };

        xExtent.min = Math.min(xExtent.min, edgeCoordinates.left);
        xExtent.max = Math.max(xExtent.max, edgeCoordinates.right);
        yExtent.min = Math.min(yExtent.min, edgeCoordinates.top);
        yExtent.max = Math.max(yExtent.max, edgeCoordinates.bottom);
      });

      width = xExtent.max - xExtent.min;
      height = yExtent.max - yExtent.min;
    }

    if (yExtent.min < 0) {
      offsetTop = offsetTop + Math.abs(yExtent.min);
    }
    if (xExtent.min < 0) {
      offsetLeft = offsetLeft + Math.abs(xExtent.min);
    }

    return { width, height, offsetTop, offsetLeft };
  }

  container: HTMLDivElement | null = null;
  clone: HTMLElement | null = null;

  private createImageContainer(width: number, height: number, offsetTop: number, offsetLeft: number) {
    let { rootElement } = this.args;

    if (rootElement == null) {
      return null;
    }

    let container = document.createElement('div');
    container.style.setProperty('position', 'relative');

    let clone = this.prepareCanvasClone(width, height, offsetTop, offsetLeft);

    if (clone == null) {
      return null;
    }

    container.appendChild(clone);

    this.container = container;
    this.clone = clone;

    rootElement.appendChild(container);

    return container;
  }

  private cleanUp() {
    this.clone?.remove();
    this.container?.remove();

    this.clone = null;
    this.container = null;
    this.hasRenderedAllItems = false;
    this.previousRenderCheckTimestamp = 0;
    this.originalZoomLevel = this.zoomManager.zoomLevel;
    this.originalCanvasScrollPosition = {
      top: 0,
      left: 0,
    };
    this.shouldResetZoomLevel = false;
  }

  private prepareCanvasClone(width: number, height: number, offsetTop: number, offsetLeft: number) {
    let { canvasElement } = this.args;
    if (canvasElement == null) {
      return null;
    }

    // We need to inline the SVG, rather than use an img tag, because rendering
    // the workflow as an image uses a canvas, which can't handle cross-origin
    // requests (and assets are served from the unityassets subdomain)
    let logoString = svgJar('logo-color-horizontal', {
      class: 'block absolute bottom-[20px] right-[20px]',
      stroke: 'none',
      width: '217px',
      height: '40px',
    });

    let clone = canvasElement.cloneNode(true) as HTMLElement;
    // This replaces the SVG dot background from the real canvas with a simpler
    // CSS-only with 1px squares instead, which looks almost identical.
    // The SVG background sometimes wouldn't render, especially in Firefox or
    // Safari, but this one renders consistently.
    clone.style.setProperty('background', GRAY_100);
    clone.style.setProperty('background-image', `radial-gradient(${GRAY_300},1px,transparent 0)`);
    clone.style.setProperty('background-size', '20px 20px');
    clone.style.setProperty('width', `${width}px`);
    clone.style.setProperty('height', `${height}px`);
    clone.style.setProperty('overflow', 'visible');
    // Set clone to 100% zoom level
    clone.style.setProperty('--scale', '1');
    // Set custom margins on the clone's workflow to add extra space on all
    // sides
    clone.style.setProperty('--offsetX', `${offsetLeft}px`);
    clone.style.setProperty('--offsetY', `${offsetTop}px`);
    // Make sure all arrow paths are 1px wide
    clone.style.setProperty('--pathStrokeWidth', '1px');
    // Don't scroll the clone container to make sure the workflow fits in the
    // center
    clone.scrollLeft = 0;
    clone.scrollTop = 0;

    // The graph container element has an initial opacity of 0 which then
    // animates to full opacity. We need to reset it to 1, because the animation
    // won't trigger in the clone, and the workflow wouldn't render.
    let graphContainer = clone.querySelector('.js-vwb-graph-container') as HTMLElement | null;
    if (graphContainer != null) {
      graphContainer.style.setProperty('opacity', '1');
      graphContainer.style.setProperty('animation', 'none');
    }

    // eslint-disable-next-line spellcheck/spell-checker
    clone.insertAdjacentHTML('beforeend', logoString);

    return clone;
  }

  renderElementToImage = task({ drop: true }, async (element: HTMLElement, width: number, height: number) => {
    let htmlToImage = await import('html-to-image');

    let fontEmbedCSS = await htmlToImage.getFontEmbedCSS(element);
    let data = await htmlToImage.toPng(element, {
      fontEmbedCSS,
      width,
      height,
      canvasWidth: width,
      canvasHeight: height,
      filter: (element) => {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (element == null || element.classList == null || this.stickyNotesManager.isShowingStickyNotes) {
          return true;
        }
        // Don't include sticky notes in export if they're hidden
        return !element.hasAttribute('[data-vwb-sticky-note]');
      },
    });

    return data;
  });

  exportImage = task({ drop: true }, async () => {
    this.analytics.track('campaign', 'workflow_image_export_started', {
      model: this.args.campaign,
      trayTab: this.args.tab ?? VWBTab.Build,
    });

    try {
      this.isExportingImage = true;

      await this.ensureWorkflowRendered.perform();

      let { width, height, offsetTop, offsetLeft } = this.getSize();

      let container = this.createImageContainer(width, height, offsetTop, offsetLeft);
      if (container == null) {
        return;
      }

      await this.resetZoomIfNecessary.perform();

      let imageData = await this.renderElementToImage.perform(container, width, height);
      saveAs(imageData, `customerio-workflow-${dasherize(this.args.campaign.name)}.png`);
    } catch (e) {
      this.raven.captureException(e);
      this.alerts.error('workflow_export_as_image_error', 'Something went wrong while exporting the workflow image');
    } finally {
      this.cleanUp();

      this.isExportingImage = false;
    }
  });
}
